import React, { useContext, useMemo } from 'react';

import { FadeInComponent } from '@/components/FadeInComponent';
import { CorePermissions, LicenseType, Permissions, ProductType } from '@/core/api/collections/users';
import { AuthContext } from '@/core/auth/AuthContext';
import { ProductPermissionGroup } from '@/core/users/UserPermissions/ProductPermissionGroup';
import { ProductPermissionRow } from '@/core/users/UserPermissions/ProductPermissionRow';
import { PermissionError, usePermissions } from '@/core/users/UserPermissions/usePermissions';
import { GRID_CLASSES, GRID_COLUMNS } from '@/core/users/UserPermissions/UserPermissionsContentWidth';
import { ReactComponent as LicenseContributorIcon } from '@/icons/license_contributor.svg';
import { ReactComponent as LicenseSupportIcon } from '@/icons/license_support.svg';
import { ReactComponent as CoreLogo } from '@/icons/uservoice_logo_simple.svg';
import { ReactComponent as ValidationLogo } from '@/icons/validation_logo_simple.svg';
import { ReactComponent as WarningIcon } from '@/icons/warning.svg';

const PERMISSION_ROW_WIDTH = 520;

type Props = {
  permissions: Permissions;
  coreDisabledMessage?: string;
  uvkDisabledMessage?: string;
  onCorePermissionChanged: <K extends keyof CorePermissions>(key: K, value: CorePermissions[K]) => void;
  toggleAccess: (product: ProductType) => void;
}
export function EditPermissions ({
  permissions: initialPermissions,
  coreDisabledMessage,
  uvkDisabledMessage,
  onCorePermissionChanged: onCorePermissionChangedCallback,
  toggleAccess,
}: Props) {
  const { hasAccessTo, hasCoreAccess, hasUvkAccess } = useContext(AuthContext);
  const permissions = usePermissions(initialPermissions);
  const settingsSubtext = useMemo(() => {
    const settingsSubtextParts = ['General'];
    if (hasAccessTo('web_portal')) {
      settingsSubtextParts.push('Web Portal');
    }
    if (hasAccessTo('sidebar')) {
      settingsSubtextParts.push('Contributor Sidebar');
    }
    if (hasAccessTo('widgets')) {
      settingsSubtextParts.push('Widget');
    }
    if (settingsSubtextParts.length > 1) {
      settingsSubtextParts[settingsSubtextParts.length - 1] = `and ${settingsSubtextParts[settingsSubtextParts.length - 1]} settings`;
    }
    const delimiter = settingsSubtextParts.length === 2 ? ' ' : ', ';
    return `${settingsSubtextParts.join(delimiter)}.`;
  }, []);
  const discoverySubtext = useMemo(() => {
    if (hasAccessTo('web_portal') || hasAccessTo('widgets')) {
      return 'Gather, aggregate, analyze, and follow through on feedback from customers and internal teams.';
    }
    return 'Gather, aggregate, analyze, and follow through on feedback from internal teams.';
  }, []);

  const licenseType = initialPermissions.core?.licenseType;

  const onCorePermissionChanged: Props['onCorePermissionChanged'] = (key, value) => {
    // if a permission was successfully changed, we reset the error message
    permissions.setError(undefined);
    onCorePermissionChangedCallback(key, value);
  };

  return (
    <FadeInComponent
      layout="position"
      className={`py-16 ${GRID_CLASSES}`}
      style={{ gridTemplateColumns: GRID_COLUMNS }}
    >
      {hasCoreAccess && (
        <ProductPermissionGroup
          product="discovery"
          description={discoverySubtext}
          renderLogo={() => (
            <div className="flex items-center justify-center h-full rounded bg-salmon-200 text-secondary">
              <CoreLogo className="w-5 h-7" />
            </div>
          )}
          disabledMessage={coreDisabledMessage}
          hasAccess={!!initialPermissions.core && initialPermissions.core.state !== 'deleted'}
          onAccessToggled={() => toggleAccess('discovery')}
          licenseType={licenseType}
        >
          <ProductPermissionRow
            title="Capture Feedback"
            description="Capture feedback on behalf of customers."
            options={{
              rw: { label: 'Always on', isSelected: true },
            }}
            onSelect={() => {}}
          />
          {hasAccessTo('features_feature') && (
            <ProductPermissionRow
              title={hasAccessTo('internal_roadmap') ? 'Internal Roadmap' : 'Features'}
              description={hasAccessTo('internal_roadmap') ?
                'Access to edit and manage the Internal Roadmap.' :
                'Access to edit and manage features.'
              }
              options={permissions.core.features}
              unavailableMessage={licenseType === 'fullaccess_admin' ? undefined : (
                <UnavailablePermissionsMessage licenseType={licenseType} />
              )}
              disabledMessage={coreDisabledMessage}
              onSelect={option => {
                if (option === 'rw' && permissions.core.feedback.no?.isSelected) {
                  permissions.setError(PermissionError.ROADMAP_RW_IDEAS_REQUIRED);
                } else {
                  onCorePermissionChanged('features', option);
                }
              }}
            />
          )}
          <ProductPermissionRow
            title="Idea Management"
            description="Access to view and manage ideas."
            options={permissions.core.feedback}
            unavailableMessage={licenseType === 'fullaccess_admin' ? undefined : (
              <UnavailablePermissionsMessage licenseType={licenseType} />
            )}
            disabledMessage={coreDisabledMessage}
            onSelect={option => {
              if (option === 'no' && permissions.core.features.rw?.isSelected) {
                permissions.setError(PermissionError.IDEAS_NO_ROADMAP_REQUIRED);
              } else {
                onCorePermissionChanged('feedback', option);
              }
            }}
          />
          { hasAccessTo('ticketing_system') && (
            <>
              <ProductPermissionRow
                title="Articles"
                description="Access to view and manage articles."
                options={permissions.core.knowledgebase}
                unavailableMessage={licenseType !== 'contributor' ? undefined : (
                  <UnavailablePermissionsMessage licenseType={licenseType} />
                )}
                disabledMessage={coreDisabledMessage}
                onSelect={option => onCorePermissionChanged('knowledgebase', option)}
              />
              <ProductPermissionRow
                title="Tickets"
                description="Access to view and manage the ticketing system."
                options={permissions.core.tickets}
                unavailableMessage={licenseType !== 'contributor' ? undefined : (
                  <UnavailablePermissionsMessage licenseType={licenseType} />
                )}
                disabledMessage={coreDisabledMessage}
                onSelect={option => onCorePermissionChanged('tickets', option)}
              />
            </>
          )}
          <ProductPermissionRow
            title="Settings"
            description={settingsSubtext}
            options={permissions.core.settings}
            unavailableMessage={licenseType !== 'contributor' ? undefined : (
              <UnavailablePermissionsMessage licenseType={licenseType} />
            )}
            disabledMessage={coreDisabledMessage}
            onSelect={option => onCorePermissionChanged('settings', option)}
          />
          {permissions.error && (
            <FadeInComponent className="col-start-4 col-span-full">
              <div className="flex items-center p-5 text-sm font-light rounded bg-salmon-100 text-error" style={{ width: PERMISSION_ROW_WIDTH }}>
                <WarningIcon className="w-4 h-4 mr-3" />
                {permissions.error}
              </div>
            </FadeInComponent>
          )}
        </ProductPermissionGroup>
      )}
      {(hasCoreAccess && hasUvkAccess) && <Divider />}
      {hasUvkAccess && (
        <ProductPermissionGroup
          product="validation"
          description="Perform scalable and repeatable product research to validate your ideas."
          renderLogo={() => (
            <div className="flex items-center justify-center h-full bg-blue-200 rounded text-secondary">
              <ValidationLogo className="w-6 h-6" />
            </div>
          )}
          disabledMessage={uvkDisabledMessage}
          hasAccess={!!initialPermissions.uvk && initialPermissions.uvk.state !== 'deleted'}
          onAccessToggled={() => toggleAccess('validation')}
        >
          <ProductPermissionRow
            title="Public Roadmap"
            description="Access to view and manage roadmaps and features."
            options={{
              rw: { label: 'Always on', isSelected: true },
            }}
            onSelect={() => {}}
          />
        </ProductPermissionGroup>
      )}
    </FadeInComponent>
  );
}

function UnavailablePermissionsMessage ({ licenseType }: { licenseType?: LicenseType }) {
  let Icon;
  let label;
  switch (licenseType) {
    case 'contributor':
      Icon = <LicenseContributorIcon width={13} height={19} />;
      label = 'Contributor';
      break;
    default:
      Icon = <LicenseSupportIcon width={15} height={15} />;
      label = 'Support';
  }
  return (
    <FadeInComponent className="flex items-center text-sm text-gray-600 col-span-3">
      <div className="flex items-center p-5 text-sm font-light text-gray-600 bg-gray-100 rounded" style={{ width: PERMISSION_ROW_WIDTH }}>
        { Icon }
        <div className="ml-3">
          Users with a {label} license <span className="mx-px font-bold">do not</span> have access to this feature.
        </div>
      </div>
    </FadeInComponent>
  );
}

function Divider () {
  return (
    <div className="border-b border-gray-300 col-start-2 col-span-full" />
  );
}
